import React from "react";
import PersonAvatar from "../home/schedule_components/PersonAvatar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DownloadIcon from "@mui/icons-material/Download";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import FormSubmissionReviewModal from "./FormSubmissionReviewModal";
import FormGradeModal from "./FormGradeModal";
import { isDisplayOnlyType } from "../create_training_content/formFieldHelpers";

function FormRecordTable({ record, onBack, employeeAvatarColorByUserId, homeCompany }) {
  const fields = record.fields || [];
  const scoring = !!record.scoring_enabled;
  const [reviewSubmission, setReviewSubmission] = React.useState(null);
  const [gradeSubmission, setGradeSubmission] = React.useState(null);

  // Submissions are held locally so a saved grade updates the row in place
  // without a full record refetch. Resync if the parent reloads the record.
  const [submissions, setSubmissions] = React.useState(record.submissions || []);
  React.useEffect(() => {
    setSubmissions(record.submissions || []);
  }, [record]);

  // A submission is gradable when it carries a manual-grading state — driven by
  // the submission itself (its snapshot), not the form's current fields, so a
  // pending row stays gradable even if the form was edited after submission.
  const isGradableSubmission = (s) =>
    s.grading_status === "pending" || s.grading_status === "graded";

  // Merge a saved grade back into the matching submission row.
  // - date_completed is left untouched: a grade preserves the learner's
  //   submission updated_at, so the completion date stays correct.
  // - graded_at IS refreshed to the new value: it's the concurrent-grade token,
  //   which a grade bumps, so a subsequent re-grade from this row isn't falsely
  //   rejected as "graded by someone else".
  const handleGraded = (submission, completion, newGradedAt) => {
    if (!completion) return;
    setSubmissions((prev) =>
      prev.map((s) =>
        s.store_team_member_id === submission.store_team_member_id
          ? {
              ...s,
              score: completion.score,
              is_passed: completion.is_passed,
              grading_status: completion.grading_status,
              field_scores: completion.field_scores,
              graded_at: newGradedAt !== undefined ? newGradedAt : s.graded_at,
            }
          : s
      )
    );
  };

  const formatDateReadable = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderResponseValue = (field, responses) => {
    const v = (responses || {})[field.id];
    if (field.type === "checkbox") return v ? "Yes" : "No";
    if (v === undefined || v === null || v === "") return "—";
    return String(v);
  };

  // A failed gated submission is returned in submissions (shown "Not passed")
  // but the learner is intentionally not counted as completed, so they're also
  // in incomplete_assignments. Filter those out so a learner never appears as
  // both a submission row and a "Not completed" row (mirrors the quiz table).
  const submittedEmployeeIds = new Set(
    (submissions || []).map((s) => s.employee_id)
  );
  const incompleteAssignments = (record.incomplete_assignments || []).filter(
    (a) => !submittedEmployeeIds.has(a.employee_id)
  );

  const hasSubmissions = submissions && submissions.length > 0;
  const hasIncomplete = incompleteAssignments.length > 0;
  const hasData = hasSubmissions || hasIncomplete;

  const exportFormCSV = () => {
    // Build the column set from the union of each submission's OWN field defs
    // (snapshot), falling back to the current fields. This keeps historical
    // exports accurate: renaming/removing/reordering fields after submissions
    // no longer blanks or mislabels prior rows. One column per field id; each
    // row's values are rendered with that row's own definition.
    const columns = [];
    const seenIds = new Set();
    const addDefs = (defs) =>
      (defs || []).forEach((f) => {
        // Skip display-only headings — they have no response, so a column for
        // one would export blank "—" cells for every row.
        if (!f || !f.id || isDisplayOnlyType(f.type) || seenIds.has(f.id)) return;
        seenIds.add(f.id);
        columns.push({ id: f.id, label: f.label || f.id });
      });
    (submissions || []).forEach((s) =>
      addDefs(s.fields && s.fields.length ? s.fields : fields)
    );
    addDefs(fields); // include current fields (covers no-submission / incomplete-only)

    // Disambiguate header labels against each other and the fixed meta columns,
    // so a field labeled e.g. "Score" (or two ids sharing a label after edits)
    // can't silently overwrite another column. objectToCSV derives its header
    // row from the first row's keys, so every row must carry every column.
    const headerById = {};
    const usedHeaders = new Set(["Employee", "Score", "Passed", "Completed By", "Date"]);
    columns.forEach((c) => {
      let h = c.label;
      let n = 2;
      while (usedHeaders.has(h)) h = `${c.label} (${n++})`;
      usedHeaders.add(h);
      headerById[c.id] = h;
    });

    const blankColumns = () => {
      const obj = {};
      columns.forEach((c) => {
        obj[headerById[c.id]] = "";
      });
      return obj;
    };

    const rows = [];
    (submissions || []).forEach((s) => {
      const rowDefs = s.fields && s.fields.length ? s.fields : fields;
      const defById = {};
      rowDefs.forEach((f) => {
        if (f && f.id) defById[f.id] = f;
      });
      const row = { Employee: s.employee_name, ...blankColumns() };
      columns.forEach((c) => {
        if (defById[c.id]) {
          row[headerById[c.id]] = renderResponseValue(defById[c.id], s.responses);
        }
      });
      if (scoring) {
        row["Score"] = s.score ?? "";
        row["Passed"] = s.is_passed === true ? "Yes" : s.is_passed === false ? "No" : "";
      }
      row["Completed By"] = s.completed_by || "";
      row["Date"] = s.date_completed ? formatDateReadable(s.date_completed) : "";
      rows.push(row);
    });
    incompleteAssignments.forEach((a) => {
      const row = { Employee: a.employee_name, ...blankColumns() };
      if (scoring) {
        row["Score"] = "";
        row["Passed"] = "";
      }
      row["Completed By"] = "";
      row["Date"] = "Not completed";
      rows.push(row);
    });
    if (rows.length === 0) return;
    downloadCSV(objectToCSV(rows), `form_${(record.name || "form").replace(/\s+/g, "_")}.csv`);
  };

  return (
    <div className="px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-violet-600 hover:text-violet-700 text-sm font-medium mb-4 transition-colors"
        >
          <ChevronLeftIcon className="-ml-2" style={{ fontSize: "24px" }} />
          Back to Records
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              {record.name}
            </h2>
            <p className="text-sm text-slate-600">Form responses</p>
          </div>
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
            onClick={exportFormCSV}
          >
            <DownloadIcon style={{ fontSize: "20px" }} />
            <span className="font-medium">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Details card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className={`grid ${scoring ? "grid-cols-4" : "grid-cols-3"} gap-6`}>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Completed By
            </div>
            <div className="text-sm font-medium text-slate-900">
              {record.completed_by_role === "manager"
                ? "Manager / supervisor"
                : "The learner"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Fields
            </div>
            <div className="text-2xl font-bold text-slate-900">{fields.length}</div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Completions
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {record.total_submissions} / {record.assigned_count}
            </div>
          </div>
          {scoring && (
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Pass Threshold
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {record.pass_threshold ?? "—"}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Responses table */}
      <div className="-mx-6 border-y border-slate-200 bg-white">
        {hasData ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Responses
                  </th>
                  {scoring && (
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Score
                    </th>
                  )}
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Completed By
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {(submissions || []).map((s, index) => (
                  <tr
                    key={`done-${index}`}
                    onClick={() =>
                      isGradableSubmission(s)
                        ? setGradeSubmission(s)
                        : setReviewSubmission(s)
                    }
                    className="hover:bg-slate-50 transition-colors duration-150 cursor-pointer"
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={s.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[s.employee_id] ||
                              "bg-slate-600"
                            }
                          />
                        </div>
                        <div className="text-xs font-medium text-slate-900">
                          {s.employee_name}
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3">
                      <div className="space-y-1 min-w-[260px]">
                        {/* Render with the fields this submission was made
                            against (snapshot), so later edits to the form don't
                            blank or mislabel it. Falls back to current fields
                            for submissions recorded before snapshots existed. */}
                        {((s.fields && s.fields.length ? s.fields : fields))
                          .filter((f) => !isDisplayOnlyType(f.type))
                          .map((f) => (
                            <div key={f.id} className="text-xs">
                              <span className="font-medium text-slate-500">
                                {f.label}:
                              </span>{" "}
                              <span className="text-slate-900">
                                {renderResponseValue(f, s.responses)}
                              </span>
                            </div>
                          ))}
                      </div>
                    </td>
                    {scoring && (
                      <td className="align-middle px-6 py-3 whitespace-nowrap">
                        {s.grading_status === "pending" ? (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                            Needs grading
                          </span>
                        ) : (
                          <>
                            <span className="text-xs font-medium text-slate-900">
                              {s.score ?? "—"}
                            </span>
                            {s.is_passed === true && (
                              <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                                Passed
                              </span>
                            )}
                            {s.is_passed === false && (
                              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                                Not passed
                              </span>
                            )}
                          </>
                        )}
                      </td>
                    )}
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <span className="text-xs text-slate-600">
                        {s.completed_by || "—"}
                      </span>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-xs text-slate-600">
                        <CalendarTodayIcon style={{ fontSize: "13px" }} />
                        {s.date_completed ? formatDateReadable(s.date_completed) : "—"}
                      </div>
                    </td>
                  </tr>
                ))}
                {incompleteAssignments.map((a, index) => (
                  <tr key={`todo-${index}`} className="hover:bg-slate-50 transition-colors duration-150">
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={a.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[a.employee_id] ||
                              "bg-slate-600"
                            }
                          />
                        </div>
                        <div className="text-xs font-medium text-slate-900">
                          {a.employee_name}
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3" colSpan={scoring ? 4 : 3}>
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                        Not completed
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <CheckCircleIcon className="mx-auto mb-3 text-slate-300" style={{ fontSize: "28px" }} />
            <p className="text-slate-400 text-sm">No assignments yet.</p>
          </div>
        )}
      </div>

      <FormSubmissionReviewModal
        open={!!reviewSubmission}
        record={record}
        submission={reviewSubmission}
        onClose={() => setReviewSubmission(null)}
      />

      <FormGradeModal
        open={!!gradeSubmission}
        record={record}
        submission={gradeSubmission}
        homeCompany={homeCompany}
        onClose={() => setGradeSubmission(null)}
        onGraded={handleGraded}
      />
    </div>
  );
}

export default FormRecordTable;
